import React, { useEffect, useState } from "react";
import { ProSidebar, Menu, MenuItem } from "react-pro-sidebar";
import {
  Box,
  Button,
  IconButton,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { Link, useNavigate, useLocation } from "react-router-dom";

import "react-pro-sidebar/dist/css/styles.css";

import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import PlaylistRemoveIcon from "@mui/icons-material/PlaylistRemove";
import ReportIcon from "@mui/icons-material/Report";
import GroupsIcon from "@mui/icons-material/Groups";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined";
// import { useAppContext } from "../../Context/UseContext";
import { GrAnalytics } from "react-icons/gr";
import { IoTicketOutline } from "react-icons/io5";

import "./Sidebar.css"; // Import custom CSS
import { tokens } from "../../theme";
import { useCookies } from "../../hooks/useCookies";

const Item = ({ title, to, icon, selected }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  return (
    <MenuItem
      active={selected === title}
      style={{
        color: colors.grey[100],
      }}
      icon={icon}
    >
      <Typography>{title}</Typography>
      <Link to={to} />
    </MenuItem>
  );
};

const Category = ({ title, icon, children, isCollapsed }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [isOpen, setIsOpen] = useState(true);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  return (
    <Box>
      <Box
        display="flex"
        alignItems="center"
        sx={{
          cursor: "pointer",
          mt: isCollapsed ? "30px" : "0",
          mb: isCollapsed ? "30px" : "0",
        }}
        onClick={handleToggle}
      >
        {icon &&
          React.cloneElement(icon, {
            sx: {
              color: colors.grey[100],
              marginLeft: "27px",
              marginTop: "10px",
            },
          })}
        {!isCollapsed && (
          <Typography
            variant="h6"
            color={colors.grey[100]}
            sx={{ m: "15px 0 5px 20px", flexGrow: 1 }}
          >
            {title}
          </Typography>
        )}
        {!isCollapsed && (
          <Box sx={{ mr: 2 }}>
            {isOpen ? (
              <ExpandLessIcon sx={{ color: colors.grey[100] }} />
            ) : (
              <ExpandMoreIcon sx={{ color: colors.grey[100] }} />
            )}
          </Box>
        )}
      </Box>
      <Box className={`dropdown-sidebar ${isOpen ? "open" : ""}`} pl={2}>
        {children}
      </Box>
    </Box>
  );
};

const Sidebar = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const { deleteCookie } = useCookies();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery("(max-width: 768px)");

  // const { loginAsAdmin, logout } = useAppContext();
  const [selected, setSelected] = useState("Approval");
  const [isCollapsed, setIsCollapsed] = useState(false); // for desktop
  const [isToggled, setIsToggled] = useState(false); // for mobile toggle

  useEffect(() => {
    const currentPath = location.pathname;

    // Optional: auto-select title based on pathname
    if (currentPath.includes("/dashboard")) setSelected("Dashboard");
    else if (currentPath.includes("/analytics")) setSelected("Analytics");
    else if (currentPath.includes("/admin/user/list")) setSelected("User List");
    else if (currentPath.includes("/admin/user/approval"))
      setSelected("Approval");
    else if (currentPath.includes("/admin/user/rejected"))
      setSelected("Rejected");
    else if (currentPath.includes("/admin/user/report")) setSelected("Reports");
    else if (currentPath.includes("/promotionalcodes"))
      setSelected("Promotional Codes");
  }, [location]);

  const handleLogout = () => {
    deleteCookie("Admintoken");
    navigate("/adminlogin");
  };

  return (
    <Box
      sx={{
        "& .pro-sidebar-inner": {
          background: `${colors.primary[400]} !important`,
        },
        "& .pro-icon-wrapper": {
          backgroundColor: "transparent !important",
        },
        "& .pro-inner-item": {
          padding: "5px 35px 5px 20px !important",
        },
        "& .pro-inner-item:hover": {
          color: "#868dfb !important",
        },
        "& .pro-menu-item.active": {
          color: "#6870fa !important",
        },
        fontFamily: '"Inter", sans-serif',
      }}
    >
      {/* Hamburger icon for mobile */}
      {isMobile && !isToggled && (
        <IconButton
          onClick={() => setIsToggled(true)}
          sx={{ position: "fixed", top: 16, left: 16, zIndex: 1400 }}
        >
          <MenuOutlinedIcon />
        </IconButton>
      )}
      <ProSidebar
        collapsed={isCollapsed}
        toggled={isToggled}
        onToggle={() => setIsToggled(!isToggled)}
        breakPoint="md"
      >
        <Menu iconShape="square">
          {!isMobile && (
            <MenuItem
              onClick={() => setIsCollapsed(!isCollapsed)}
              icon={isCollapsed ? <MenuOutlinedIcon /> : undefined}
              style={{
                margin: "10px 0 20px 0",
                color: colors.grey[100],
              }}
            >
              {!isCollapsed && (
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  ml="15px"
                >
                  <Typography
                    variant="h3"
                    color={colors.grey[100]}
                  ></Typography>
                  <IconButton onClick={() => setIsCollapsed(!isCollapsed)}>
                    <MenuOutlinedIcon />
                  </IconButton>
                </Box>
              )}
            </MenuItem>
          )}
          {isMobile && isToggled && (
            <IconButton
              onClick={() => setIsToggled(false)}
              sx={{ position: "fixed", top: 16, left: 16, zIndex: 1400 }}
            >
              <MenuOutlinedIcon />
            </IconButton>
          )}

          {!isCollapsed && (
            <Box mb="25px">
              <Box display="flex" justifyContent="center" alignItems="center">
                <img
                  alt="profile-user"
                  width="100px"
                  height="100px"
                  src={`../../assets/user.png`}
                  style={{ cursor: "pointer", borderRadius: "50%" }}
                />
              </Box>
              <Box textAlign="center">
                <Typography
                  variant="h2"
                  color={colors.grey[100]}
                  fontWeight="bold"
                  sx={{ m: "10px 0 0 0" }}
                >
                  Admin
                </Typography>
              </Box>
            </Box>
          )}

          <Box paddingLeft={isCollapsed ? undefined : "10%"}>
            <Item
              title="Dashboard"
              to="/dashboard"
              icon={<HomeOutlinedIcon />}
              selected={selected}
            />
            <Item
              title="Analytics"
              to="/analytics"
              icon={<GrAnalytics />}
              selected={selected}
            />
            <Category
              title="Users"
              icon={<PeopleOutlinedIcon />}
              isCollapsed={isCollapsed}
            >
              <Item
                title="User List"
                to="/admin/user/list"
                icon={<GroupsIcon />}
                selected={selected}
              />
              <Item
                title="Approval"
                to="/admin/user/approval"
                icon={<PendingActionsIcon />}
                selected={selected}
              />
              <Item
                title="Rejected"
                to="/admin/user/rejected"
                icon={<PlaylistRemoveIcon />}
                selected={selected}
              />
              <Item
                title="Reports"
                to="/admin/user/report"
                icon={<ReportIcon />}
                selected={selected}
              />
            </Category>

            <Item
              title="Promotional Codes"
              to="/promo-codes"
              icon={<IoTicketOutline size={16} />}
              selected={selected}
            />
            <Button
              variant="contained"
              onClick={handleLogout}
              sx={{
                width: "90%",
                mb: 2,
                mt: 2,
                mr: 6,
                py: 1.5,
                background: "linear-gradient(90deg, #FC8C66, #F76A7B)",
                color: "#FFF",
                fontWeight: "bold",
                "&:hover": {
                  background: "linear-gradient(90deg, #F76A7B, #FC8C66)",
                },
              }}
            >
              LogOut
            </Button>
          </Box>
        </Menu>
      </ProSidebar>
    </Box>
  );
};

export default Sidebar;
